import { useNavigate } from "@remix-run/react";
import type { User } from "@supabase/supabase-js";
import { CreditCard, LogOut, Package, Settings2, Users } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useLogoutOverlay } from "~/context/LogoutOverlayContext";
import { useIsAdmin } from "~/hooks/use-is-admin";
import { getFullDisplayName, getInitials } from "~/lib/display-name";
import { handleLogout } from "~/lib/logout";
import { useIsPractitioner, useUserProfile } from "~/lib/user";

import { useSupabaseSession } from "../context/SupabaseSessionContext";
import { useAnonymous } from "../hooks/use-anonymous";
import { useIsMobile } from "../hooks/use-mobile";
import { Button } from "./ui/button";
import { A } from "./ui/typography";

interface UserMenuProps {
    user?: User;
    collapsed?: boolean;
}

export function UserMenu({ collapsed = false }: { collapsed?: boolean } = {}) {
    const { data: user } = useUserProfile();
    const navigate = useNavigate();
    const profile = { fullName: user?.fullName, email: user?.email };
    const initials = getInitials(profile);
    const displayName = getFullDisplayName(profile);
    const { isAnonymous } = useAnonymous();
    const { data: isAdmin, isLoading: isAdminLoading } = useIsAdmin();
    const { isPractitioner } = useIsPractitioner();
    const { showLogoutOverlay } = useLogoutOverlay();

    const handleSignOut = () => {
        showLogoutOverlay();
        handleLogout();
    };

    const avatar = (
        <Avatar className="size-8 cursor-pointer">
            {user?.avatar_url ? (
                <AvatarImage src={user?.avatar_url} alt={displayName} />
            ) : (
                <AvatarFallback className="bg-brand-forest-100 text-brand-forest-800">
                    {initials}
                </AvatarFallback>
            )}
        </Avatar>
    );

    if (isAnonymous) {
        return (
            <Button
                variant="outline"
                onClick={() => {
                    window.location.href = `/link-account?redirectTo=${encodeURIComponent(
                        window.location.pathname,
                    )}`;
                }}
            >
                Create account
            </Button>
        );
    }

    // AIDEV-NOTE: When collapsed, show only the avatar with dropdown (no email)
    return (
        <div className="flex items-center gap-2">
            {!collapsed && (
                <div className="hidden md:max-w-[150px] xl:max-w-[10vw] truncate text-xs text-muted-foreground md:block">
                    {displayName}
                </div>
            )}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <div className="flex items-center gap-4">{avatar}</div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="p-4">
                    {isAdmin && (
                        <>
                            <DropdownMenuItem asChild className="p-2">
                                <A
                                    href="/admin"
                                    className="flex cursor-pointer items-center"
                                >
                                    <Settings2 className="mr-2 size-4" />
                                    Admin
                                </A>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                        </>
                    )}
                    <DropdownMenuItem asChild className="p-2">
                        <A
                            href="/account"
                            className="flex cursor-pointer items-center"
                        >
                            <Settings2 className="mr-2 size-4" />
                            Settings
                        </A>
                    </DropdownMenuItem>
                    {/* AIDEV-NOTE: Direct routes into the settings sections (Adam, Sep
                        2026) so the common destinations are one hop from the avatar.
                        Preferences splits by role like the settings rail does; Membership
                        is practitioner-only and, like the rail, also needs billing to be
                        running. Add-Ons is never gated — it is the way in and out of an
                        add-on. */}
                    <DropdownMenuItem asChild className="p-2">
                        <A
                            href={
                                isPractitioner
                                    ? "/practitioner/preferences"
                                    : "/account/preferences"
                            }
                            className="flex cursor-pointer items-center"
                        >
                            <Users className="mr-2 size-4" />
                            Preferences
                        </A>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="p-2">
                        <A
                            href="/account/add-ons"
                            className="flex cursor-pointer items-center"
                        >
                            <Package className="mr-2 size-4" />
                            Add-Ons
                        </A>
                    </DropdownMenuItem>
                    {isPractitioner && (
                        <DropdownMenuItem asChild className="p-2">
                            <A
                                href="/account/membership"
                                className="flex cursor-pointer items-center"
                            >
                                <CreditCard className="mr-2 size-4" />
                                Membership
                            </A>
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onClick={handleSignOut}
                        className="flex cursor-pointer items-center p-2"
                    >
                        <LogOut className="mr-2 size-4" />
                        Log out
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
